import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  CircularProgress,
  Typography,
  Tooltip,
  Card,
  CardContent,
  Divider,
  Button,
  Chip,
  TextField,
  InputAdornment,
} from "@mui/material";
import { Link } from "react-router-dom";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import LinkIcon from "@mui/icons-material/Link";
import { linkSheetToBatch } from "../../../Reducer/productBatchesSlice";
import dayjs from "dayjs";

// Define keyframes for smooth glow animation
const glowAnimation = {
  "@keyframes glow": {
    "0%": { boxShadow: "0 0 0px rgba(255,152,0,0.0)" },
    "50%": { boxShadow: "0 0 15px rgba(255,152,0,0.6)" },
    "100%": { boxShadow: "0 0 0px rgba(255,152,0,0.0)" },
  },
};

const DeficiateAlerts = ({ batchesData, loading, error }) => {
  const [animate, setAnimate] = useState(false);
  const dispatch = useDispatch();
  const { sheetGenerating } = useSelector((state) => state.productBatches);

  // Manual link state
  const [showLinkInput, setShowLinkInput] = useState(null); // stores batchId
  const [sheetUrl, setSheetUrl] = useState("");
  const [linking, setLinking] = useState(false);
  const [linkError, setLinkError] = useState("");

  useEffect(() => {
    const interval = setInterval(() => {
      setAnimate(true);
      setTimeout(() => setAnimate(false), 2000);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  const handleLinkSheet = async (batchId) => {
    if (!sheetUrl.trim()) {
      setLinkError("Please paste a URL or ID");
      return;
    }
    setLinking(true);
    setLinkError("");
    try {
      await dispatch(linkSheetToBatch({ batchId, sheetUrl: sheetUrl.trim() })).unwrap();
      setSheetUrl("");
      setShowLinkInput(null);
    } catch (err) {
      setLinkError(typeof err === "string" ? err : "Failed to link sheet");
    } finally {
      setLinking(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ mt: 4, ml: 5, minWidth: 320 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ mt: 4, ml: 5, minWidth: 320 }}>
        <Typography variant="body2" color="error">
          Error loading alerts: {error}
        </Typography>
      </Box>
    );
  }

  const alerts = batchesData.filter((batch) => batch.deficiate_count > 0);

  if (alerts.length === 0) return null;

  return (
    <Box
      sx={{
        mt: 4,
        ml: 5,
        minWidth: 500,
        maxWidth: 500,
        maxHeight: 550, // Limit height
        overflowY: "auto", // Scroll enabled
        pr: 1,
        ...glowAnimation,
        "&::-webkit-scrollbar": {
          width: "8px",
        },
        "&::-webkit-scrollbar-track": {
          background: "#f1f1f1",
          borderRadius: "4px",
        },
        "&::-webkit-scrollbar-thumb": {
          background: "#b0b0b0",
          borderRadius: "4px",
        },
        "&::-webkit-scrollbar-thumb:hover": {
          background: "#888",
        },
      }}
    >
      <Typography
        variant="h6"
        sx={{
          fontWeight: 700,
          color: "#d32f2f",
          mb: 2,
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        <WarningAmberIcon sx={{ color: "#d32f2f" }} /> Pending Data Uploads
      </Typography>

      {alerts.map((batch) => (
        <Card
          key={batch.id}
          sx={{
            mb: 2,
            borderLeft: "6px solid #ff9800",
            transition: "all 0.3s ease",
            "&:hover": { transform: "translateY(-2px)", boxShadow: 6 },
            animation: animate ? "glow 2s ease-in-out" : "none",
          }}
        >
          <CardContent sx={{ p: 2.5 }}>
            <Typography variant="subtitle1" fontWeight={600}>
              {batch?.product?.event_name}
              {batch?.variant?.name ? ` - ${batch.variant.name}` : ""}
            </Typography>

            <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mb: 1, fontWeight: 500 }}>
              📅 Created: {dayjs(batch.createdAt).format("MMM D, YYYY | h:mm A")}
            </Typography>


            <Typography
              variant="body2"
              sx={{ color: "text.secondary", mb: 1 }}
            >
              Some exhibitor data is missing for this batch.
            </Typography>

            <Divider sx={{ my: 1 }} />

            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mt: 1,
              }}
            >
              {/* Three info chips */}
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                <Chip
                  label={`Demand: ${batch.demandingCount ?? 0}`}
                  size="small"
                  sx={{ fontWeight: 600, bgcolor: "#1565c0", color: "#fff" }}
                />
                <Chip
                  label={`Verified: ${batch.available_count ?? 0}`}
                  size="small"
                  sx={{ fontWeight: 600, bgcolor: "#2e7d32", color: "#fff" }}
                />
                <Chip
                  label={`Remaining: ${batch.deficiate_count ?? 0}`}
                  size="small"
                  sx={{ fontWeight: 600, bgcolor: "#e65100", color: "#fff" }}
                />
              </Box>

              <Tooltip title="Upload Required Data" arrow>
                <Button
                  component={Link}
                  to={`/dashboard/data-rows/${batch.productRecord?.woocommerce_product_id}/${batch.product_record_id}`}
                  variant="contained"
                  color="warning"
                  size="small"
                  startIcon={<CloudUploadIcon />}
                  sx={{ fontWeight: 600 }}
                >
                  Upload
                </Button>
              </Tooltip>
            </Box>

            {/* Google Sheet Section */}
            <Divider sx={{ my: 1.5 }} />
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                flexWrap: "wrap",
              }}
            >
              {batch.google_sheet_url ? (
                <>
                  <Chip
                    label="📊 Live Sheet Active"
                    size="small"
                    sx={{
                      fontWeight: 600,
                      bgcolor: "#1b5e20",
                      color: "#fff",
                      animation: "glow 3s ease-in-out infinite",
                    }}
                  />
                  <Button
                    variant="outlined"
                    size="small"
                    color="success"
                    startIcon={<OpenInNewIcon />}
                    href={batch.google_sheet_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ fontWeight: 600, textTransform: "none" }}
                  >
                    Open Google Sheet
                  </Button>
                </>
              ) : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1, width: "100%" }}>
                  {showLinkInput === batch.id ? (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                      <TextField
                        size="small"
                        fullWidth
                        placeholder="Paste Google Sheet URL..."
                        value={sheetUrl}
                        onChange={(e) => { setSheetUrl(e.target.value); setLinkError(""); }}
                        error={!!linkError}
                        helperText={linkError}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <LinkIcon fontSize="small" color="success" />
                            </InputAdornment>
                          ),
                        }}
                        sx={{ "& .MuiOutlinedInput-root": { fontSize: "0.75rem" } }}
                      />
                      <Box sx={{ display: "flex", gap: 1 }}>
                        <Button
                          size="small"
                          variant="contained"
                          color="success"
                          onClick={() => handleLinkSheet(batch.id)}
                          disabled={linking}
                          startIcon={linking ? <CircularProgress size={12} color="inherit" /> : <LinkIcon />}
                          sx={{ textTransform: "none", fontSize: "0.75rem" }}
                        >
                          {linking ? "Linking..." : "Save Link"}
                        </Button>
                        <Button
                          size="small"
                          variant="text"
                          onClick={() => { setShowLinkInput(null); setSheetUrl(""); setLinkError(""); }}
                          sx={{ textTransform: "none", fontSize: "0.75rem", color: "#999" }}
                        >
                          Cancel
                        </Button>
                      </Box>
                    </Box>
                  ) : (
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <Button
                        variant="contained"
                        size="small"
                        color="success"
                        startIcon={<LinkIcon />}
                        onClick={() => setShowLinkInput(batch.id)}
                        sx={{ fontWeight: 600, textTransform: "none" }}
                      >
                        Link Sheet
                      </Button>
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          </CardContent>
        </Card>
      ))}
    </Box>
  );
};

export default DeficiateAlerts;

